import { useState } from 'react';
import {
  CategoryUnionType,
  CompanyLinkType,
  mapCompanyTagType,
  useFindCompanyTagsSuspense,
} from '@api';
import {
  CompanyLinkFormValuesType,
  useCreateCompanyLink,
  useUpdateCompanyLink,
} from '@common';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { Grid } from '@mantine/core';
import {
  AddCompanyLinkMenu,
  CompanyLinkCard,
  CompanyLinksModal,
  FreelancerFormSchemaType,
  OpenedModalUnionType,
} from './components';
import { getArtistsForCompanyLink } from './helpers';
import { useCompanyLinksData, useCreateFreelancer } from './hooks';
import classes from './CompanyLinks.module.css';

export const CompanyLinks: React.FC = () => {
  const { personDetails, artists, companySites, companyLinkCategories } =
    useCompanyLinksData();
  const { createCompanyLink } = useCreateCompanyLink();
  const { createFreelancer } = useCreateFreelancer();
  const { updateCompanyLink } = useUpdateCompanyLink();
  const { hasPermission } = usePermissionContext();
  const { data: companyTags } = useFindCompanyTagsSuspense({
    query: {
      select: (data) => data.data.map(mapCompanyTagType),
    },
  });

  const [openedModal, setOpenedModal] = useState<OpenedModalUnionType | null>(
    null
  );
  const [companyLinkToEdit, setCompanyLinkToEdit] =
    useState<CompanyLinkType | null>(null);

  const closeModal = () => {
    setCompanyLinkToEdit(null);
    setOpenedModal(null);
  };

  const handleCreateCompanyLink = async (data: CompanyLinkFormValuesType) => {
    await createCompanyLink(
      {
        ...data,
        personId: personDetails.id,
        categories: data.categories as CategoryUnionType[],
      },
      closeModal
    );
  };

  const handleEditAndAddFreelancer = async (data: FreelancerFormSchemaType) => {
    await createFreelancer(personDetails.id, data, closeModal);
  };

  const handleEditCompanyLink = async (data: CompanyLinkFormValuesType) => {
    if (!companyLinkToEdit) return;
    await updateCompanyLink({
      companyLink: companyLinkToEdit,
      data,
      onSuccessCallback: closeModal,
    });
  };

  const openEditCompanyLinkModal = (companyLink: CompanyLinkType) => {
    setCompanyLinkToEdit(companyLink);
    if (companyLink.type === 'BUSINESS') {
      setOpenedModal('EDIT_COMPANY_LINK');
    } else {
      setOpenedModal('EDIT_FREELANCER');
    }
  };

  const userCanEditPerson = hasPermission({
    permission: 'ADDRESSBOOK_PERSONS',
    action: 'WRITE',
  });

  return (
    <>
      <Grid
        columns={12}
        gap="md"
        className={classes.root}
        data-testid="company-links"
      >
        {personDetails.companies.map((companyLink) => (
          <CompanyLinkCard
            key={companyLink.linkId}
            personId={personDetails.id}
            companyLink={companyLink}
            categoriesWithArtists={getArtistsForCompanyLink(
              artists,
              companyLink.companyId
            )}
            onEdit={() => openEditCompanyLinkModal(companyLink)}
          />
        ))}
        {userCanEditPerson && (
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <AddCompanyLinkMenu
              onAddCompanyLinkClick={() => setOpenedModal('ADD_COMPANY_LINK')}
              onAddFreelancerClick={() => setOpenedModal('ADD_FREELANCER')}
            />
          </Grid.Col>
        )}
      </Grid>
      <CompanyLinksModal
        openedModal={openedModal}
        onClose={closeModal}
        handleCreateCompanyLink={handleCreateCompanyLink}
        handleEditAndAddFreelancer={handleEditAndAddFreelancer}
        handleEditCompanyLink={handleEditCompanyLink}
        companySites={companySites}
        companyLinkCategories={companyLinkCategories}
        companyLinkToEdit={companyLinkToEdit}
        companyTags={companyTags}
      />
    </>
  );
};
